import PageHero from "@/components/PageHero";
import { Link } from "react-router-dom";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import { IMPACT_STATS, TESTIMONIALS, PRESS, FAQ, IMG } from "@/lib/data";
import PartnersStrip from "@/components/PartnersStrip";
import { Plus, Minus, Accessibility, ShieldCheck, FileText } from "lucide-react";
import { useState } from "react";

export default function Impact() {
  return (
    <div data-testid="impact-page">
      <SEOHead
        title="Impact, Accessibility & Safeguarding · Temple of Kulture"
        description="120k+ audiences reached, 9 cultural pillars, 300+ workshops delivered. Read our impact data, partner testimonials, press coverage and accessibility, safeguarding & EDI commitments."
        image={IMG.dance1}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <PageHero
        eyebrow="Impact & Community"
        title={
          <>
            Measured. <br />
            <span className="text-amber-400">Felt.</span> Shared.
          </>
        }
        subtitle="Our impact is cultural, social and emotional. Here's what we've built so far — and the partners, press and policies behind it."
        image={IMG.dance1}
        testId="impact-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {IMPACT_STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.05}>
                <div className="border border-white/5 bg-zinc-950 p-8">
                  <p className="font-display font-black text-5xl sm:text-6xl text-amber-400 leading-none">
                    {s.value}
                  </p>
                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-zinc-400">
                    {s.label}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 border-y border-amber-500/10 bg-black">
        <Container>
          <FadeIn>
            <SectionLabel>Testimonials</SectionLabel>
            <SectionHeading className="mt-6 max-w-3xl">
              What people <br />
              <span className="text-amber-400">say</span> after.
            </SectionHeading>
          </FadeIn>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.06}>
                <blockquote className="border border-white/5 bg-zinc-950 p-8 h-full flex flex-col justify-between">
                  <p className="text-zinc-200 text-lg leading-relaxed font-display">
                    “{t.quote}”
                  </p>
                  <footer className="mt-8 pt-6 border-t border-white/5">
                    <p className="text-amber-400 font-bold text-sm">{t.name}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-[0.18em] mt-1">
                      {t.role}
                    </p>
                  </footer>
                </blockquote>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <section id="partners" className="py-24 scroll-mt-24">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16">
            <FadeIn>
              <SectionLabel>Press</SectionLabel>
              <SectionHeading className="mt-6">In the press.</SectionHeading>
              <ul className="mt-10 grid grid-cols-2 gap-3">
                {PRESS.map((p) => (
                  <li
                    key={p}
                    className="border border-white/5 bg-zinc-950 px-5 py-4 text-zinc-300 text-sm uppercase tracking-[0.2em] hover:border-amber-400/40 hover:text-amber-400 transition-colors"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn delay={0.1}>
              <SectionLabel>Partners & funders</SectionLabel>
              <SectionHeading className="mt-6">
                Backed by <span className="text-amber-400">institutions</span>.
              </SectionHeading>
              <p className="mt-6 text-zinc-300 text-sm leading-relaxed max-w-md">
                Funded, hosted and shaped in partnership with the venues,
                councils, funders and youth networks that already build the
                cultural fabric of Medway, Kent and the wider UK.
              </p>
              <div className="mt-8">
                <PartnersStrip variant="card" withContainer={false} eyebrow={null} title={null} copy={null} testId="impact-partners" />
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>

      <section className="py-24 border-y border-amber-500/10 bg-black">
        <Container>
          <FadeIn className="mb-12">
            <SectionLabel>Trust & governance</SectionLabel>
            <SectionHeading className="mt-6 max-w-3xl">
              Cinematic outside, <br />
              <span className="text-amber-400">safeguarded</span> inside.
            </SectionHeading>
            <p className="mt-6 text-zinc-300 max-w-2xl leading-relaxed">
              We work with children, families, schools, councils, NHS partners
              and immersive technology. Our policies hold all of that to the
              same standards as the creative work.
            </p>
          </FadeIn>
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              {
                Icon: Accessibility,
                t: "Accessibility",
                d: "Wheelchair access, captioning, relaxed performances, sensory-aware sessions across our programme.",
                slug: "accessibility",
              },
              {
                Icon: ShieldCheck,
                t: "Safeguarding",
                d: "All practitioners are DBS-checked, trained in safeguarding and aligned with KCSiE / Working Together guidance.",
                slug: "safeguarding",
              },
              {
                Icon: FileText,
                t: "Policies",
                d: "Safeguarding, GDPR, EDI, immersive safety, refunds and governance — published, reviewed annually.",
                slug: "policies",
              },
            ].map((b, i) => (
              <FadeIn key={b.t} delay={i * 0.08}>
                <Link
                  to="/policies"
                  data-testid={`impact-trust-card-${b.slug}`}
                  className="group block border border-white/5 bg-zinc-950 p-8 h-full hover:border-amber-400/40 transition-colors"
                >
                  <b.Icon className="h-7 w-7 text-amber-400" />
                  <h3 className="mt-5 font-display font-bold text-xl">{b.t}</h3>
                  <p className="mt-3 text-zinc-300 leading-relaxed text-sm">
                    {b.d}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 tok-cta-label text-amber-400 group-hover:gap-3 transition-all">
                    Read in full
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24">
        <Container>
          <FadeIn>
            <SectionLabel>FAQ</SectionLabel>
            <SectionHeading className="mt-6 max-w-3xl">
              Questions <span className="text-amber-400">answered.</span>
            </SectionHeading>
          </FadeIn>
          <div className="mt-12 max-w-4xl">
            {FAQ.map((item, i) => (
              <FaqItem key={item.q} {...item} index={i} />
            ))}
          </div>
        </Container>
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="What's next"
        steps={[
          {
            label: "Partners & funders",
            title: "Partner with TOK",
            desc: "Schools, councils, NHS, brands — let's design something together.",
            cta: "Partnerships",
            to: "/corporate",
            testid: "impact-next-partnerships",
          },
          {
            label: "Trust pack",
            title: "Read every policy",
            desc: "Safeguarding, GDPR, immersive safety and accessibility in one place.",
            cta: "Read policies",
            to: "/policies",
            testid: "impact-next-policies",
          },
          {
            label: "Support our work",
            title: "Join the mailing list / donate",
            desc: "Become part of the world we're building — and help us tell more untold stories.",
            cta: "Join",
            to: "/join",
            testid: "impact-next-join",
          },
        ]}
      />
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full py-6 flex items-center justify-between gap-6 text-left hover:text-amber-400 transition-colors"
      >
        <span className="font-display font-bold text-lg sm:text-xl">{q}</span>
        {open ? (
          <Minus className="h-5 w-5 shrink-0 text-amber-400" />
        ) : (
          <Plus className="h-5 w-5 shrink-0 text-amber-400" />
        )}
      </button>
      <div
        className={`grid transition-all duration-500 ${
          open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-zinc-300 leading-relaxed max-w-3xl">{a}</p>
        </div>
      </div>
    </div>
  );
}
